'use client';

import * as React from 'react';
import { IconChevronRight } from '@tabler/icons-react';
import { useTranslations } from 'next-intl';
import { Link } from '@/i18n/routing';
import { PanelResizeHandle } from '@/components/panel-resize-handle';
import {
  Infobar,
  InfobarContent,
  InfobarGroup,
  InfobarGroupContent,
  InfobarHeader,
  InfobarRail,
  InfobarTrigger,
  useInfobar
} from '@/components/ui/infobar';
import { usePanelWidth } from '@/hooks/use-panel-width';
import {
  INFO_SIDEBAR_BOUNDS,
  INFO_SIDEBAR_DEFAULT_WIDTH
} from '@/lib/panel-resize';

/** Info sidebar sizing. Duplicated in dashboard/layout.tsx, which cannot
 *  import from this 'use client' module on the server — keep them in sync. */

export function InfoSidebar({
  ...props
}: React.ComponentProps<typeof Infobar>) {
  const { content } = useInfobar();
  const t = useTranslations('common.infobar');
  const tCommon = useTranslations('common');
  const { handleProps } = usePanelWidth('info-sidebar', {
    defaultWidth: INFO_SIDEBAR_DEFAULT_WIDTH,
    min: INFO_SIDEBAR_BOUNDS.min,
    max: INFO_SIDEBAR_BOUNDS.max,
    // Sits at the page's inline END, so the handle is on its inline-START
    // edge — the mirror of the nav sidebar.
    edge: 'start'
  });

  // Default/fallback data when no page has set content
  const defaultData = {
    title: t('title'),
    sections: [
      {
        title: t('gettingStarted'),
        description: t('gettingStartedDesc'),
        links: [
          {
            title: t('installationGuide'),
            url: '#'
          }
        ]
      }
    ]
  };

  const data = content || defaultData;

  return (
    <Infobar {...props}>
      <InfobarHeader className='flex flex-row items-start justify-between gap-2 border-b px-3 py-3'>
        <div className='min-w-0 flex-1'>
          <h2 className='text-lg font-semibold wrap-break-word'>
            {data.title}
          </h2>
        </div>
        <div className='shrink-0'>
          <InfobarTrigger className='-me-1' />
        </div>
      </InfobarHeader>
      <InfobarContent>
        <InfobarGroup>
          <InfobarGroupContent>
            <div className='flex flex-col gap-6 px-4 py-4'>
              {data.sections && data.sections.length > 0 ? (
                data.sections.map((section, index) => (
                  <div key={index} className='flex flex-col gap-3'>
                    {section.title && (
                      <h3 className='text-foreground text-sm font-semibold'>
                        {section.title}
                      </h3>
                    )}
                    {section.description && (
                      <p className='text-muted-foreground text-sm leading-relaxed'>
                        {section.description}
                      </p>
                    )}
                    {section.links && section.links.length > 0 && (
                      <div className='flex flex-col gap-2'>
                        <h4 className='text-muted-foreground text-xs font-medium tracking-wide uppercase'>
                          {t('learnMore')}
                        </h4>
                        <ul className='flex flex-col gap-1.5'>
                          {section.links.map((link, linkIndex) => (
                            <li key={linkIndex}>
                              <Link
                                href={link.url}
                                className='text-primary flex items-center gap-1.5 text-sm underline'
                                target='_blank'
                              >
                                <span>{link.title}</span>
                                <IconChevronRight className='h-3 w-3' />
                              </Link>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                ))
              ) : (
                <div className='text-muted-foreground px-2 py-4 text-center text-sm'>
                  {t('noContent')}
                </div>
              )}
            </div>
          </InfobarGroupContent>
        </InfobarGroup>
      </InfobarContent>
      <InfobarRail
        aria-label={tCommon('toggleInfobar')}
        title={tCommon('toggleInfobar')}
      />
      <PanelResizeHandle
        className='absolute inset-y-0 start-0 group-data-[state=collapsed]:hidden'
        {...handleProps}
      />
    </Infobar>
  );
}
